import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ukStamp as fmtWhen } from "@/lib/time";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Building2, ChevronLeft, ChevronUp, Handshake, Loader2, Minus,
  Radio, Search, Send, MessageSquare, MessageSquarePlus, ArrowUpRight,
} from "lucide-react";

/**
 * ChatDock — the desk, always in the corner of the Hub.
 *
 * Presence is real: `/hub/presence` reads `users.seen_at`, which every authenticated
 * request stamps, so a lender with their portal open shows as online and nobody can
 * fake it. Collapsed it still carries the live count and the unread badge, so the
 * desk is never silently missing a message.
 */
const STATE_DOT = {
  online: "bg-teal-500",
  idle:   "bg-[#7da9ff]",
  away:   "bg-slate-300",
};
const STATE_WORD = { online: "online now", idle: "idle", away: "away" };
const STORE_KEY = "cx_hub_dock_open";

export default function ChatDock() {
  // The Messages page *is* this dock, full size. Floating a second copy over it covered the
  // inbox's own send button and gave you two chat surfaces arguing on one screen, so the dock
  // stands down while you're in there.
  const { pathname } = useLocation();
  const onInbox = pathname.startsWith("/hub/messages");
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem(STORE_KEY) === "1"; } catch { return false; }
  });
  const [data, setData] = useState(null);
  // The open conversation is held here as `{partner, thread_id}` — deliberately NOT derived from
  // the polled list by thread id. `/hub/presence` returns `thread_id: null` for every partner who
  // has a login but no conversation yet, so `items.find(i => i.thread_id === activeId)` matched
  // one of those the moment Back set the id to null: the panel stayed on a thread and Back (and
  // Escape) looked dead. Holding the conversation in state means closing it cannot be undone by
  // whatever the next poll returns, and a freshly created thread can't vanish either.
  const [chat, setChat] = useState(null);
  const [q, setQ] = useState("");
  const [opening, setOpening] = useState(null);

  const pull = useCallback(async () => {
    try { const r = await api.get("/hub/presence"); setData(r.data); }
    catch { /* silent — the dock must never shout */ }
  }, []);

  useEffect(() => {
    pull();
    const t = setInterval(pull, open ? 12000 : 25000);
    return () => clearInterval(t);
  }, [pull, open]);

  useEffect(() => {
    try { localStorage.setItem(STORE_KEY, open ? "1" : "0"); } catch { /* private mode */ }
  }, [open]);

  // Escape always gets you out: first out of the conversation, then out of the dock.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      setChat((cur) => { if (cur) return null; setOpen(false); return cur; });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const items = data?.items || [];
  const online = items.filter((i) => i.presence === "online");
  const unread = data?.unread || 0;
  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const s = q.toLowerCase();
    return items.filter((i) => (i.name || "").toLowerCase().includes(s) || (i.email || "").toLowerCase().includes(s));
  }, [items, q]);
  const active = chat
    ? { ...(items.find((i) => i.kind === chat.partner.kind && i.party_id === chat.partner.party_id)
            || chat.partner),
        // the id we opened with wins — presence can lag a brand-new thread by a poll
        thread_id: chat.thread_id }
    : null;

  const openPartner = async (p) => {
    if (p.thread_id) { setChat({ partner: p, thread_id: p.thread_id }); return; }
    setOpening(p.party_id);
    try {
      const r = await api.post("/hub/chat/threads", { kind: p.kind, party_id: p.party_id });
      setChat({ partner: p, thread_id: r.data.id });
      pull();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't open that conversation"); }
    finally { setOpening(null); }
  };

  if (onInbox) return null;

  return (
    <div className="pointer-events-none fixed bottom-0 right-0 z-40 hidden p-5 lg:block" data-testid="hub-chat-dock">
      <AnimatePresence mode="wait" initial={false}>
        {open ? (
          <motion.div
            key="panel"
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ type: "spring", stiffness: 420, damping: 34 }}
            className="hub-dock-shadow pointer-events-auto flex h-[560px] max-h-[calc(100vh-120px)] w-[392px] flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white/95 backdrop-blur-xl"
            data-testid="hub-chat-dock-panel"
          >
            {/* Head */}
            <div className="flex items-center gap-2.5 border-b border-slate-200/70 bg-[#0B1220] px-4 py-3 text-white">
              <span className="relative flex size-2">
                <span className="absolute inline-flex size-full animate-ping rounded-full bg-teal-400 opacity-70" />
                <span className="relative inline-flex size-2 rounded-full bg-teal-400" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-[12.5px] font-semibold tracking-[-0.01em]">The desk</div>
                <div className="truncate text-[10.5px] text-white/55">
                  {online.length > 0 ? `${online.length} partner${online.length === 1 ? "" : "s"} online` : "nobody signed in right now"}
                  {unread > 0 ? ` · ${unread} waiting on you` : ""}
                </div>
              </div>
              <Link to="/hub/messages" className="grid size-7 place-items-center rounded-lg text-white/60 transition-colors hover:bg-white/10 hover:text-white"
                    aria-label="Open the full inbox" data-testid="hub-dock-full">
                <ArrowUpRight className="size-3.5" />
              </Link>
              <button onClick={() => setOpen(false)} className="grid size-7 place-items-center rounded-lg text-white/60 transition-colors hover:bg-white/10 hover:text-white"
                      aria-label="Minimise the desk" data-testid="hub-dock-minimise">
                <Minus className="size-4" />
              </button>
            </div>

            {active ? (
              <Thread key={active.thread_id} partner={active} onBack={() => setChat(null)} onSent={pull} />
            ) : (
              <>
                <div className="border-b border-slate-100 p-2.5">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-slate-400" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a lender or introducer…"
                           className="h-9 w-full rounded-xl border border-slate-200 bg-slate-50 pl-9 pr-3 text-[12.5px] text-slate-900 placeholder:text-slate-400 focus:border-[#0055FF]/40 focus:bg-white focus:outline-none"
                           data-testid="hub-dock-search" />
                  </div>
                </div>
                <div className="hub-scroll flex-1 overflow-y-auto" data-testid="hub-dock-list">
                  {!data ? (
                    <div className="grid place-items-center py-16 text-slate-300"><Loader2 className="size-5 animate-spin" /></div>
                  ) : filtered.length === 0 ? (
                    <div className="px-6 py-14 text-center text-[12px] text-slate-400">
                      <MessageSquare className="mx-auto mb-2 size-5 text-slate-300" />
                      No partners match that.
                    </div>
                  ) : filtered.map((p) => (
                    <button key={`${p.kind}-${p.party_id}`} onClick={() => openPartner(p)}
                            className="flex w-full items-center gap-3 border-b border-slate-100 px-4 py-3 text-left transition-colors hover:bg-slate-50"
                            data-testid={`hub-dock-partner-${p.party_id}`}>
                      <Avatar p={p} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="truncate text-[12.5px] font-semibold text-slate-900">{p.name}</span>
                          {p.unread > 0 && (
                            <span className="hub-num grid h-4 min-w-4 shrink-0 place-items-center rounded-full bg-[#0055FF] px-1 text-[9.5px] font-bold text-white"
                                  data-testid={`hub-dock-unread-${p.party_id}`}>{p.unread}</span>
                          )}
                        </div>
                        <div className="truncate text-[10.5px] text-slate-400">
                          {p.kind === "lender" ? "Lender" : "Introducer"} · {STATE_WORD[p.presence]}
                          {p.last_body
                            ? ` · ${p.last_sender === "admin" ? "you: " : ""}${p.last_body}`
                            : !p.thread_id ? " · not started" : ""}
                        </div>
                      </div>
                      {opening === p.party_id && <Loader2 className="size-3.5 animate-spin text-slate-300" />}
                    </button>
                  ))}
                </div>
              </>
            )}
          </motion.div>
        ) : (
          <motion.button
            key="bar"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ type: "spring", stiffness: 420, damping: 32 }}
            onClick={() => setOpen(true)}
            className="hub-dock-shadow pointer-events-auto group flex items-center gap-3 rounded-full border border-white/10 bg-[#0B1220] py-2.5 pl-4 pr-3 text-white"
            data-testid="hub-chat-dock-bar"
          >
            <span className="relative flex size-2">
              <span className="absolute inline-flex size-full animate-ping rounded-full bg-teal-400 opacity-70" />
              <span className="relative inline-flex size-2 rounded-full bg-teal-400" />
            </span>
            <span className="text-[12px] font-semibold tracking-[-0.01em]">The desk</span>
            <span className="hidden text-[11px] text-white/55 xl:inline" data-testid="hub-dock-online-count">
              {online.length > 0 ? `${online.length} online` : "quiet"}
            </span>
            {online.length > 0 && (
              <span className="flex items-center -space-x-1.5">
                {online.slice(0, 3).map((p) => (
                  <span key={p.party_id}
                        className="grid size-6 place-items-center rounded-full border border-[#0B1220] bg-white/10 text-[9.5px] font-bold text-white"
                        title={p.name}>
                    {initials(p.name)}
                  </span>
                ))}
                {online.length > 3 && <span className="hub-num pl-3 text-[10px] text-white/50">+{online.length - 3}</span>}
              </span>
            )}
            {unread > 0 && (
              <span className="hub-num grid h-5 min-w-5 place-items-center rounded-full bg-[#0055FF] px-1.5 text-[10px] font-bold"
                    data-testid="hub-dock-bar-unread">{unread}</span>
            )}
            <span className="grid size-7 place-items-center rounded-full bg-white/10 transition-colors group-hover:bg-white/20">
              <ChevronUp className="size-3.5" />
            </span>
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}

function Avatar({ p, size = "size-9" }) {
  return (
    <span className="relative shrink-0">
      <span className={`grid ${size} place-items-center rounded-xl border text-slate-500 ${
        p.kind === "lender" ? "border-[#0055FF]/20 bg-[#0055FF]/[0.07] text-[#0055FF]" : "border-teal-200 bg-teal-50 text-teal-700"}`}>
        {p.kind === "lender" ? <Building2 className="size-4" /> : <Handshake className="size-4" />}
      </span>
      <span className={`absolute -bottom-0.5 -right-0.5 size-2.5 rounded-full border-2 border-white ${STATE_DOT[p.presence] || STATE_DOT.away}`}
            data-testid={`hub-dock-presence-${p.party_id}`} />
    </span>
  );
}

/**
 * A conversation nobody has started yet.
 *
 * A blank panel tells you nothing and quietly invites you to close it again, so this says who
 * you're about to reach, where it lands, and gives three openers worth sending — one tap each,
 * straight into the composer so you can still change every word before it goes.
 */
const OPENERS = {
  lender: [
    "Anything you're especially hungry for this month? I'll make sure it reaches you first.",
    "Quick check — is the criteria we hold for you still accurate?",
    "Is anything slowing your decisions down at the moment? Happy to change how we send them.",
  ],
  referrer: [
    "Anything in your pipeline you'd like a view on before you submit it?",
    "Quick check — is everything working the way you'd expect in your portal?",
    "Want me to walk you through how the routing decides which desks see a case?",
  ],
};

function EmptyChat({ partner, onPick }) {
  const openers = OPENERS[partner.kind] || OPENERS.referrer;
  return (
    <div className="flex h-full flex-col items-center justify-center px-4 py-2 text-center" data-testid="hub-dock-empty">
      <span className="grid size-9 place-items-center rounded-full border border-[#0055FF]/20 bg-[#0055FF]/[0.06]">
        <MessageSquarePlus className="size-4 text-[#0055FF]" />
      </span>
      <div className="mt-2.5 max-w-[17rem] break-words text-[12.5px] font-semibold text-slate-900">
        No conversation with {partner.name} yet
      </div>
      <p className="mt-1 max-w-[17rem] text-[11px] leading-relaxed text-slate-400">
        Whatever you send lands in their {partner.kind === "lender" ? "desk" : "introducer"} portal
        and by email, and they can reply to either.
      </p>
      <div className="mt-3.5 w-full space-y-1.5">
        {openers.map((text, i) => (
          <button key={i} onClick={() => onPick(text)} data-testid={`hub-dock-opener-${i}`}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-left text-[11px] leading-snug text-slate-600 transition-colors hover:border-[#0055FF]/40 hover:bg-[#0055FF]/[0.04] hover:text-slate-900">
            {text}
          </button>
        ))}
      </div>
      <div className="mt-2.5 text-[10px] text-slate-400">Tap one to load it — nothing sends until you do.</div>
    </div>
  );
}

function Thread({ partner, onBack, onSent }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scroller = useRef(null);
  const box = useRef(null);
  // Held in a ref so a parent's inline callback can't rebuild `load` and restart the poll.
  const onSentRef = useRef(onSent);
  useEffect(() => { onSentRef.current = onSent; }, [onSent]);

  const useOpener = (text) => {
    setInput(text);
    box.current?.focus();
  };

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/hub/chat/threads/${partner.thread_id}/messages`);
      setMessages(r.data.items || []);
      onSentRef.current?.();
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [partner.thread_id]);

  useEffect(() => {
    setLoading(true); load();
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [load]);

  const stick = useRef(true);
  useEffect(() => {
    const el = scroller.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const send = async () => {
    const body = input.trim();
    if (!body || sending) return;
    stick.current = true;
    setSending(true);
    try {
      await api.post(`/hub/chat/threads/${partner.thread_id}/messages`, { body });
      setInput("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "That didn't send"); }
    finally { setSending(false); }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="hub-dock-thread">
      <div className="flex items-center gap-2.5 border-b border-slate-100 px-3 py-2.5">
        <button onClick={onBack} className="flex h-7 shrink-0 items-center gap-0.5 rounded-lg pl-1 pr-1.5 text-[11px] font-semibold text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900"
                aria-label="Back to partners" title="Back to the partner list (Esc)" data-testid="hub-dock-back">
          <ChevronLeft className="size-4" /> Back
        </button>
        <Avatar p={partner} size="size-8" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[12.5px] font-semibold text-slate-900">{partner.name}</div>
          <div className="truncate text-[10.5px] text-slate-400">
            {partner.kind === "lender" ? "Lender" : "Introducer"} · {STATE_WORD[partner.presence]}
          </div>
        </div>
        {partner.presence === "online" && (
          <span className="inline-flex items-center gap-1 rounded-full border border-teal-200 bg-teal-50 px-2 py-0.5 text-[9.5px] font-semibold uppercase tracking-wider text-teal-700">
            <Radio className="size-2.5" /> Live
          </span>
        )}
      </div>

      <div ref={scroller}
           onScroll={(e) => { const el = e.currentTarget; stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80; }}
           className="hub-scroll flex-1 space-y-2.5 overflow-y-auto bg-slate-50/70 px-3.5 py-3.5" data-testid="hub-dock-messages">
        {loading ? (
          <div className="grid h-full place-items-center text-slate-300"><Loader2 className="size-5 animate-spin" /></div>
        ) : messages.length === 0 ? (
          <EmptyChat partner={partner} onPick={useOpener} />
        ) : messages.map((m) => {
          if (m.sender === "system") {
            return (
              <div key={m.id} className="flex justify-center">
                <div className="max-w-[85%] rounded-xl border border-teal-200/70 bg-teal-50/70 px-2.5 py-1.5 text-center text-[10.5px] leading-relaxed text-teal-800">
                  {m.body}
                </div>
              </div>
            );
          }
          const mine = m.sender === "admin";
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className="max-w-[80%]">
                <div className={`whitespace-pre-wrap break-words rounded-2xl px-3 py-2 text-[12.5px] leading-relaxed ${
                  mine ? "rounded-br-md bg-[#0B1220] text-white" : "rounded-bl-md border border-slate-200 bg-white text-slate-800"}`}>
                  {m.body}
                  {m.attachment && (
                    <div className={`mt-1.5 truncate text-[10.5px] ${mine ? "text-white/60" : "text-slate-400"}`}>
                      Attached: {m.attachment.filename}
                    </div>
                  )}
                </div>
                <div className={`mt-0.5 text-[9.5px] text-slate-400 ${mine ? "text-right" : ""}`}>
                  {mine ? "You" : m.sender_name} · {fmtWhen(m.created_at)}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-end gap-2 border-t border-slate-200/70 bg-white p-2.5">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          rows={1}
          placeholder={`Message ${partner.name}…`}
          ref={box}
          className="max-h-24 flex-1 resize-none rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-[12.5px] text-slate-900 placeholder:text-slate-400 focus:border-[#0055FF]/40 focus:bg-white focus:outline-none"
          data-testid="hub-dock-composer"
        />
        <button onClick={send} disabled={sending || !input.trim()}
                className="grid size-9 shrink-0 place-items-center rounded-xl bg-[#0055FF] text-white transition-colors hover:bg-[#0044CC] disabled:opacity-40"
                data-testid="hub-dock-send">
          {sending ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
        </button>
      </div>
    </div>
  );
}

function initials(name) {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "–";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

